import React, { useCallback } from 'react';

import { AlineTrackedChangeType, AlineTransforms, Queries } from '@aline/common';
import type { DocParts } from '@aline/common';
import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { Typography } from '@mui/material';
import { ConfirmationDialog, ConfirmationDialogVariant } from 'src/components';
import { useDocumentParams } from 'src/helpers';
import { useGetCurrentDoc } from 'src/hooks';
import { useEditor } from 'src/layout-engine';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import { useInitiateDocxExport } from './useInitiateDocxExport';

type UnsupportedTrackedChangeType = Exclude<
  AlineTrackedChangeType,
  | AlineTrackedChangeType.InsertText
  | AlineTrackedChangeType.RemoveText
  | AlineTrackedChangeType.FormatText
  | AlineTrackedChangeType.TabEdit
  | AlineTrackedChangeType.TableEdit
>;
export const unsupportedTrackedChangesVar = makeVar<{
  open: boolean;
  unsupportedChanges: UnsupportedTrackedChangeType[];
}>({ open: false, unsupportedChanges: [] });

const useStyles = makeStyles()(theme => ({
  listItem: {
    ...getTypographyForSx(theme.typography.subtitle1),
    marginBottom: theme.spacing(1),
  },
}));

export function UnsupportedTrackedChangesDialog(): JSX.Element | null {
  const editor = useEditor();
  const { classes } = useStyles();
  const { document } = useGetCurrentDoc();
  const { documentId } = useDocumentParams();
  const { initiateDocxExport } = useInitiateDocxExport();
  const { open, unsupportedChanges } = useReactiveVar(unsupportedTrackedChangesVar);

  function onClose(): void {
    unsupportedTrackedChangesVar({ open: false, unsupportedChanges });
  }

  return (
    <ConfirmationDialog
      TransitionProps={{ onExit: onClose }}
      message="Detected some tracked changes that are unsupported in DOCX export. See below and continue to export."
      open={open}
      primaryText="Export"
      secondaryText="Cancel"
      title="Unsupported tracked changes detected"
      variant={ConfirmationDialogVariant.Warn}
      onClickPrimary={async () => {
        onClose();
        await initiateDocxExport(documentId, {
          docParts: editor.children,
          name: document?.name || 'Untitled document',
        });
      }}
      onClickSecondary={onClose}
      onClose={onClose}
    >
      <ol>
        {unsupportedChanges.map((changeType, index) => {
          return (
            <li key={index} className={classes.listItem}>
              <Typography variant="subtitle1">{getChangeTypeDisplayString(changeType)}</Typography>
            </li>
          );
        })}
      </ol>
    </ConfirmationDialog>
  );
}

export function getUnsupportedTrackedChangeTypes(
  changeTypes: AlineTrackedChangeType[]
): UnsupportedTrackedChangeType[] {
  return changeTypes.flatMap(changeType => {
    switch (changeType) {
      case AlineTrackedChangeType.InsertText:
      case AlineTrackedChangeType.RemoveText:
      case AlineTrackedChangeType.FormatText:
      case AlineTrackedChangeType.TabEdit:
      case AlineTrackedChangeType.InstructionFieldEdit:
      case AlineTrackedChangeType.TableEdit:
      case AlineTrackedChangeType.ParagraphMarkEdit:
        return [];
      default:
        return [changeType];
    }
  });
}

export function getChangeTypeDisplayString(changeType: AlineTrackedChangeType): string {
  switch (changeType) {
    case AlineTrackedChangeType.InsertText:
      return 'Insert text';
    case AlineTrackedChangeType.RemoveText:
      return 'Delete text';
    case AlineTrackedChangeType.FormatText:
      return 'Format text';
    case AlineTrackedChangeType.TabEdit:
      return 'Tab change';
    case AlineTrackedChangeType.BreakEdit:
      return 'Insert or remove break';
    case AlineTrackedChangeType.TableEdit:
      return 'Table change';
    case AlineTrackedChangeType.LinkEdit:
      return 'Link change';
    case AlineTrackedChangeType.DocumentEdit:
      return 'Document change';
    case AlineTrackedChangeType.FieldEdit:
      return 'Field change';
    case AlineTrackedChangeType.FootersEdit:
      return 'Footers change';
    case AlineTrackedChangeType.HeadersEdit:
      return 'Headers change';
    case AlineTrackedChangeType.ParagraphEdit:
      return 'Paragraph format change';
    case AlineTrackedChangeType.ParagraphMarkEdit:
      return 'Paragraph split or merge';
    case AlineTrackedChangeType.DrawingEdit:
      return 'Drawing change';
    case AlineTrackedChangeType.InstructionFieldEdit:
      return 'Instruction field change';
    case AlineTrackedChangeType.CheckboxEdit:
      return 'Checkbox change';
  }
}

export function useDownloadDOCXWithTrackedChangesCheck() {
  const { initiateDocxExport } = useInitiateDocxExport();
  const downloadDOCXWithTrackedChangesCheck = useCallback(
    async (documentId: UUID, options: { docParts: DocParts; name: string }) => {
      const { docParts } = options;
      const editor = AlineTransforms.createServerEditorFromJson(docParts, {
        state: {
          editingUserId: null,
          isInSigningCeremony: false,
          isReadOnly: false,
          isSigned: false,
        },
      });
      const trackedChangeTypes = Queries.getTrackedChangeTypes(editor);
      const unsupportedChangeTypes = getUnsupportedTrackedChangeTypes(trackedChangeTypes);
      if (unsupportedChangeTypes.length)
        return unsupportedTrackedChangesVar({
          open: true,
          unsupportedChanges: unsupportedChangeTypes,
        });

      await initiateDocxExport(documentId, options);
    },
    [initiateDocxExport]
  );
  return { downloadDOCXWithTrackedChangesCheck: downloadDOCXWithTrackedChangesCheck };
}
